<template>
  <div
    class="modalBlock"
    :class="{
      'modalBlock-hasBackgroundImage': hasModalBackgroundImage
    }"
  >
    <StageBorder></StageBorder>
    <div
      class="modalHeaderBlock"
      :class="{ 'modalHeaderBlock-isHidden': modalHeaderBlockIsHidden }"
    >
      <div class="headerTitle">
        {{ comboName }}
      </div>

      <div class="header-composition">
        <ComboProductImage
          :scale="1"
          v-if="isCombo"
          :components="comboComponents"
        ></ComboProductImage>
        <ingredient-image-composition
          :scale="1"
          v-else-if="hasComposerPositionData"
          :product="selectedItem"
          :compositionImageGroups="compositionImageGroups"
        ></ingredient-image-composition>
        <img v-else class="headerImg" :src="selectedItem.image" />
      </div>

      <div
        class="yellowCircle"
        :class="[
          hasHeaderBackgroundImage ? 'yellowCircle-hasBackgroundImage' : ''
        ]"
      ></div>
    </div>

    <div class="fixedContainer" v-if="isV2EditOrder">
      <EditOverflowScroll
        :maxListHeight="maxListHeight"
        ref="EditOverflowScroll"
      >
        <header-nav-edit
          v-if="isCombo"
          :activeCombo="currentCombo"
          :currentComponent="currentComponent"
          @changeComponent="changeComponentRequested"
          :marginTop="0"
        />
        <div v-if="hasIngredients" class="ingredientSelectorWrapper">
          <IngredientSelector
            ref="IngredientSelectorV2"
            :activeItems="activeItems"
            :isEdit="isEdit"
            :product="selectedItem"
            :hasDefaultIngredients="hasDefaultIngredients"
            @change="changeIngredient"
            @disableProceedByMinCount="disableProceedByMinCount"
            @composerPositionData="handleComposerPositionData"
            @ingredientSelectorState="onIngredientSelectorState"
          ></IngredientSelector>
        </div>
        <div
          class="changeComboBlock"
          v-if="activeItems.length && isComponentEditable"
        >
          <div class="comboHeader">
            {{ $t(`edit.combo`) }}
          </div>
          <div ref="productBlock" class="productBlock productBlock-v2">
            <product-list
              :isEdit="isEdit"
              :activeItem="selectedItem"
              :itemIds="itemIds"
              @onAddBasket="changeComponent"
              @onShowIngredientWizard="onShowIngredientWizard"
              @onConfirmIngredientWizard="onHideIngredientWizard"
              @onCloseIngredientWizard="onHideIngredientWizard"
              :menuOffset="true"
              :showInfoIcon="false"
            />
          </div>
        </div>
        <div class="actionBlock" v-if="showProductEditButtons">
          <div class="closeBtn text" @click="closeModal">
            {{ $t(`edit.btn.close`) }}
          </div>
          <button
            :disabled="saveDisabled"
            class="headerSave background"
            @click="saveItem"
          >
            {{ $t(`edit.btn.save`) }}
          </button>
        </div>
      </EditOverflowScroll>
    </div>
    <div v-else>
      <div>
        <div
          v-if="hasIngredients"
          class="ingredientSelectorWrapper"
          :class="{
            half: activeItems.length
          }"
        >
          <IngredientSelector
            ref="IngredientSelector"
            :isEdit="isEdit"
            :product="selectedItem"
            :hasDefaultIngredients="hasDefaultIngredients"
            @change="changeIngredient"
            @disableProceedByMinCount="disableProceedByMinCount"
            @composerPositionData="handleComposerPositionData"
          ></IngredientSelector>
        </div>
        <div
          v-else-if="atLeastOneAvailableProductHasDefaultIngredients()"
          class="half"
        ></div>
        <div
          class="changeComboBlock"
          v-if="activeItems.length && !hasIngredientGroups"
        >
          <div class="comboHeader">
            {{ $t(`edit.combo`) }}
          </div>
          <div ref="productBlock" class="productBlock">
            <product-list
              v-if="this.productBlockHeight > 0"
              :activeItem="selectedItem"
              :itemIds="itemIds"
              @onAddBasket="changeComponent"
              :menuOffset="true"
              :showInfoIcon="false"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      class="actionBlock actionBlock-v2"
      :class="{ 'actionBlock-v2-disabled': wizardEditButtonsDisabled }"
      v-if="isV2EditOrder && showWizardEditButtons"
    >
      <deny-button @onDeny="back" :text="$t(`edit.wizard.back`)" />
      <save-button
        @onSave="saveItem"
        :text="$t(`edit.wizard.done`)"
        width="340px"
        :disabled="saveDisabled"
      />
      <confirm-button
        @onConfirm="next"
        :text="$t(`edit.wizard.go_on`)"
        :disabled="isNextDisabled"
      />
    </div>
    <div class="actionBlock" v-if="!isV2EditOrder">
      <div class="closeBtn text" @click="closeModal">
        {{ $t(`edit.btn.close`) }}
      </div>
      <button
        :disabled="saveDisabled"
        class="headerSave background"
        @click="saveItem"
      >
        {{ $t(`edit.btn.save`) }}
      </button>
    </div>
    <div class="BottomNavigationContainer" ref="BottomNavigationContainer">
      <bottom-nav
        @proceed="$emit('payOnEdit')"
        :show-total-container="true"
        :show-scan-and-go="false"
        :show-submit="true"
        >{{ $t("order.btn.to_pay") }}
      </bottom-nav>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import { toCalculatable, toPrintable } from "@/utils/pricing";
import {
  addProductToBasket,
  createNewObject,
  getPopulatedIngredientGroups,
  productToBasketItem
} from "@/utils/products";
import ProductList from "@/components/menu/ProductList.vue";
import ComboProductImage from "../combo/ComboProductImage";
import IngredientSelector from "./common/ingredientSelector";
import _, { cloneDeep, isEmpty } from "lodash";
import {
  getTranslatedMenuName,
  getTranslation,
  isBackgroundImageSet,
  setFixedPrice
} from "@/utils/helpers";
import BottomNav from "@/components/BottomNav";
import IngredientImageComposition from "@/components/common/IngredientImageComposition";
import { generateCompositionImageGroups } from "@/utils/compositionImageUtil";
import StageBorder from "@/components/common/StageBorder";
import HeaderNavEdit from "@/components/combo/HeaderNavEdit.vue";
import { isV2EditOrder } from "@/utils/templateSettings";
import SaveButton from "@/components/modals/common/saveButton.vue";
import DenyButton from "@/components/modals/common/denyButton.vue";
import ConfirmButton from "@/components/modals/common/confirmButton.vue";
import EditOverflowScroll from "@/components/common/EditOverflowScroll";
import { selectionValid } from "@/utils/ingredientWizard";

export default {
  components: {
    ConfirmButton,
    DenyButton,
    EditOverflowScroll,
    SaveButton,
    HeaderNavEdit,
    IngredientImageComposition,
    IngredientSelector,
    ProductList,
    // MultiProductList,
    ComboProductImage,
    BottomNav,
    StageBorder
  },
  props: ["comboInfo", "singleItem"],
  data() {
    return {
      maxListHeight: 950,
      top: isV2EditOrder() ? 150 : 170,
      activeName: "",
      hasComposerPositionData: false,
      isCombo: false,
      isEdit: true,
      comboType: "",
      comboComponents: [],
      activeItems: [],
      currentCombo: {},
      currentComponent: {},
      selectedItem: {},
      productBlockHeight: -1,
      saveDisabled: false,
      compositionImageGroups: [],
      modalHeaderBlockIsHidden: false,
      wizardEditButtonsDisabled: false,
      ingredientSelectorState: {},
      checkEditorOverflowDebounced: {}
    };
  },
  created() {
    this.checkEditorOverflowDebounced = _.debounce(
      this.checkEditorOverflow,
      250
    );
    if (this.comboInfo.comboUID) {
      this.isCombo = true;
      this.currentCombo = this.basket.find(
        item => item.uid === this.comboInfo.comboUID
      );

      this.logAudit({
        category: "CART",
        event: "OPEN_EDIT",
        data: this.currentCombo
      });
      this.comboComponents = this.currentCombo.components.map(item =>
        createNewObject(item)
      );
      this.currentComponent = this.currentCombo.components.find(
        component => component.id === this.comboInfo.component_id
      );
      if (this.currentComponent) this.activeName = this.currentComponent.name;

      this.selectedItem = createNewObject(this.currentComponent.selected);
      this.setActiveCombo(this.currentCombo);
      this.comboType = this.currentCombo.type;
      const available_product_ids = this.currentComponent.available_products.map(
        item => item.id
      );
      this.activeItems = this.productsList.filter(
        item => available_product_ids.indexOf(item.id) !== -1
      );
      // TODO remove for first component also in theme v1???
      if (this.currentComponent.order === 1) {
        console.log("this.currentComponent.order === 1");
        //this.activeItems = [];
      }
      if (isV2EditOrder() && !this.hasIngredients) {
        this.next();
      }
    } else {
      this.selectedItem = cloneDeep(this.singleItem);
      this.setCompositionImageGroups();
      this.logAudit({
        category: "CART",
        event: "OPEN_EDIT",
        data: this.selectedItem
      });
    }
    setTimeout(this.calcProductBlockHeight, 0);
  },
  computed: {
    ...mapGetters({
      basket: "BASKET",
      productsList: "PRODUCTS_FILTERED",
      activeCombo: "ACTIVE_COMBO",
      productByUuid: "PRODUCT_BY_UUID"
    }),
    itemIds() {
      return this.activeItems.map(item => item.id);
    },
    isV2EditOrder,
    hasModalBackgroundImage() {
      return isBackgroundImageSet(
        "--component_modals_edit_modalBlock_background-image"
      );
    },
    hasHeaderBackgroundImage() {
      return isBackgroundImageSet(
        "--component_modals_edit_yellowCircle_background-image"
      );
    },
    isComponentEditable() {
      if (
        isV2EditOrder() &&
        this.isCombo &&
        this.currentCombo &&
        this.currentComponent
      ) {
        return this.currentComponent.order > 1;
      } else {
        return true;
      }
    },
    isFirstComponent() {
      if (this.isCombo && this.currentCombo && this.currentComponent) {
        return this.currentComponent.order === 1;
      }
      return false;
    },
    isLastComponent() {
      if (this.isCombo && this.currentCombo && this.currentComponent) {
        let lastComponent = this.comboComponents.concat().pop();
        return this.currentComponent.id === lastComponent.id;
      }
      return false;
    },
    isNextDisabled() {
      if (!this.isCombo && this.hasIngredientGroups) {
        return this.isLastIngredient;
      }
      if (this.isLastComponent && this.hasIngredientGroups) {
        return this.isLastIngredient;
      }
      return this.isLastComponent;
    },
    comboName() {
      if (this.isCombo) {
        let combo = this.basket.find(
          item => item.uid === this.comboInfo.comboUID
        );
        return getTranslatedMenuName(this.$store, {
          ...combo,
          components: [...this.comboComponents]
        });
      }

      return getTranslation(
        this.$store,
        "Product",
        "display_name",
        this.selectedItem.id,
        this.selectedItem.name
      );
    },
    hasDefaultIngredients() {
      return (
        !isEmpty(this.selectedItem.ingredients) &&
        !isEmpty(this.selectedItem.ingredients.default)
      );
    },
    hasIngredientGroups() {
      if (isEmpty(this.selectedItem.ingredients)) return false;
      const filtered = Object.keys(this.selectedItem.ingredients)
        .filter(key => !["default"].includes(key))
        .reduce((obj, key) => {
          return {
            ...obj,
            [key]: this.selectedItem.ingredients[key]
          };
        }, {});
      return !isEmpty(filtered);
    },
    hasIngredients() {
      return this.hasDefaultIngredients || this.hasIngredientGroups;
    },
    isFirstIngredient() {
      return this.ingredientSelectorState["isFirstIngredientGroup"];
    },
    isLastIngredient() {
      return this.ingredientSelectorState["isLastIngredientGroup"];
    },
    showProductEditButtons() {
      return !this.isCombo && !this.hasIngredientGroups;
    },
    showWizardEditButtons() {
      return this.isCombo || this.hasIngredientGroups;
    }
  },
  methods: {
    checkEditorOverflow() {
      if (this.$refs.EditOverflowScroll) {
        setTimeout(() => {
          this.$refs.EditOverflowScroll.checkOverflown();
        }, 250);
      }
    },
    scrollToTopOfEditor() {
      if (this.$refs.EditOverflowScroll) {
        this.$refs.EditOverflowScroll.scrollToTop();
      }
    },
    onIngredientSelectorState(state) {
      this.scrollToTopOfEditor();
      this.ingredientSelectorState = state;
      this.checkEditorOverflowDebounced();
    },
    back() {
      this.modalHeaderBlockIsHidden = false;
      if (this.isCombo && this.hasIngredientGroups) {
        if (!this.isFirstIngredient) {
          this.previousIngredient();
        } else {
          this.previousComponent();
        }
      } else if (this.isCombo) {
        this.previousComponent();
      } else {
        this.previousIngredient();
      }
      if (!this.isCombo && this.hasIngredients && this.isFirstIngredient) {
        this.closeModal();
      }
    },
    next() {
      this.modalHeaderBlockIsHidden = false;
      if (this.isCombo && this.hasIngredientGroups) {
        if (!this.isLastIngredient) {
          this.nextIngredient();
        } else {
          this.nextComponent();
        }
      } else if (this.isCombo) {
        this.nextComponent();
      } else {
        this.nextIngredient();
      }
    },
    previousComponent() {
      if (this.isFirstComponent) {
        this.closeModal();
        return;
      }

      let currentOrderIndex = this.currentComponent.order;
      let previousOrderIndex = currentOrderIndex - 1;
      let previousComponent = this.comboComponents.find(comp => {
        return comp.order === previousOrderIndex;
      });
      if (
        previousComponent.order === 1 &&
        isEmpty(getPopulatedIngredientGroups(previousComponent.selected))
      ) {
        this.closeModal();
        return;
      }
      if (previousComponent) {
        this.changeComponentRequested({ id: previousComponent.id, back: true });
        setTimeout(() => {
          if (
            this.$refs.IngredientSelectorV2 &&
            this.$refs.IngredientSelectorV2.ingredientGroups
          ) {
            this.$refs.IngredientSelectorV2.setActiveGroupIdx(
              this.$refs.IngredientSelectorV2.ingredientGroups.length - 1
            );
            this.$refs.IngredientSelectorV2.checkIngResize();
          }
        }, 0);
      }
    },
    nextComponent() {
      console.log("next component");
      if (this.isLastComponent) {
        return;
      }
      let currentOrderIndex = this.currentComponent.order;
      let nextOrderIndex = currentOrderIndex + 1;
      let nextComponent = this.comboComponents.find(comp => {
        return comp.order === nextOrderIndex;
      });
      if (nextComponent) {
        this.changeComponentRequested({ id: nextComponent.id });
        this.activateFirstItemOfIngredientSelector();
      }
    },
    previousIngredient() {
      if (!this.$refs.IngredientSelectorV2) {
        return;
      }
      let currentGroupIdx = this.$refs.IngredientSelectorV2.activeGroupIdx;
      let previousGroupIdx = currentGroupIdx - 1;
      if (previousGroupIdx >= 0) {
        this.$refs.IngredientSelectorV2.setActiveGroupIdx(previousGroupIdx);
      }
    },
    nextIngredient() {
      if (!this.$refs.IngredientSelectorV2) {
        return;
      }
      let igs = this.$refs.IngredientSelectorV2.ingredientGroups;
      let currentGroupIdx = this.$refs.IngredientSelectorV2.activeGroupIdx;

      let nextGroupIdx = currentGroupIdx + 1;
      if (nextGroupIdx < igs.length) {
        this.$refs.IngredientSelectorV2.setActiveGroupIdx(nextGroupIdx);
      }
    },
    atLeastOneAvailableProductHasDefaultIngredients() {
      return this.currentComponent.available_products.some(ap => {
        let product = this.productByUuid(ap.id);
        return product && !isEmpty(product.default_ingredients);
      });
    },
    changeComponentRequested(payload) {
      if (this.comboComponents) {
        this.scrollToTopOfEditor();
        const result = this.comboComponents.find(
          item => item.id === payload.id
        );
        if (result.selected) {
          this.currentComponent = result;
          this.selectedItem = createNewObject(result.selected);
          if (this.currentComponent) {
            this.activeName = this.currentComponent.name;
            if (payload.breadcrumb) {
              this.activateFirstItemOfIngredientSelector();
            }
          }
          const available_product_ids = this.currentComponent.available_products.map(
            item => item.id
          );
          this.activeItems = this.productsList.filter(
            item => available_product_ids.indexOf(item.id) !== -1
          );
          setTimeout(() => {
            this.calcProductBlockHeight();
          }, 250);
          this.checkEditorOverflowDebounced();
        }
      }
    },
    activateFirstItemOfIngredientSelector() {
      if (this.$refs.IngredientSelectorV2 !== undefined) {
        this.$refs.IngredientSelectorV2.setActiveGroupIdx(0);
      }
    },
    ...mapMutations({
      setActiveCombo: "SET_ACTIVE_COMBO"
    }),
    handleComposerPositionData(hasComposerPositionData) {
      this.hasComposerPositionData = hasComposerPositionData;
    },
    setCompositionImageGroups: function() {
      this.compositionImageGroups = generateCompositionImageGroups(
        this.selectedItem
      );
    },
    getTranslation,
    calcProductBlockHeight() {
      if (this.$refs.productBlock) {
        let box = this.$refs.productBlock.getBoundingClientRect();
        const BOTTOM_NAV_HEIGHT = 134;
        const ACTION_BUTTONS_HEIGHT_V1 = 120;
        const ACTION_BUTTONS_HEIGHT_V2 = 155;
        let actionButtonHeight = this.isV2EditOrder
          ? ACTION_BUTTONS_HEIGHT_V2
          : ACTION_BUTTONS_HEIGHT_V1;
        const MARGIN = 25;
        this.productBlockHeight =
          1920 - box.top - BOTTOM_NAV_HEIGHT - actionButtonHeight - MARGIN;
      }
    },
    ...mapMutations({
      setBasket: "SET_BASKET"
    }),
    ...mapActions({ logRouter: "LOG_ROUTER", logAudit: "LOG_AUDIT" }),
    toCalculatable,
    toPrintable,
    disableProceedByMinCount(disabled) {
      console.log("edit disableProceedByMinCount disabled: " + disabled);
      this.validateIngredients(this.selectedItem);
    },
    changeIngredient({ group, ingredient, count }) {
      console.log("changeIngredient (edit)", group, ingredient, count);
      let groupName = group.id === -1 ? "default" : "" + group.id;
      let ing = this.selectedItem.ingredients[groupName].find(
        i => i.id === ingredient.id
      );

      if (ing) {
        ing.count = count;
      }
      setTimeout(this.calcProductBlockHeight, 0);
      this.setCompositionImageGroups();
      this.changeComponent(this.selectedItem);
      this.validateIngredients(this.selectedItem);
    },
    validateIngredients(item) {
      if (item && item.available_ingredient_groups) {
        console.log("validateIngredients");
        let igs = getPopulatedIngredientGroups(item);
        this.saveDisabled = igs.some(ig => !selectionValid(ig));
      }
    },
    onShowIngredientWizard() {
      this.modalHeaderBlockIsHidden = true;
      this.wizardEditButtonsDisabled = true;
    },
    onHideIngredientWizard() {
      this.modalHeaderBlockIsHidden = false;
      this.wizardEditButtonsDisabled = false;
    },
    changeComponent(item) {
      const newItem = productToBasketItem(item);
      this.selectedItem = newItem;
      this.comboComponents.map(el => {
        if (el.name === this.activeName) {
          el.selected = newItem;
        }
      });
    },
    closeModal() {
      this.$emit("closeModal");
    },
    onlyOneProductLeft(combo) {
      console.log("onlyOneProductLeft");
      const product_items = combo.components.filter(
        item => !item.selected.no_product
      );
      return product_items.length === 1;
    },
    saveItem() {
      if (this.comboInfo.comboUID) {
        let combo = this.basket.find(
          item => item.uid === this.comboInfo.comboUID
        );
        combo = createNewObject(combo);

        combo.components.forEach(item => {
          this.comboComponents.forEach(editedComboComponent => {
            if (editedComboComponent.id === item.id) {
              item.selected = productToBasketItem(
                createNewObject(editedComboComponent.selected)
              );
            }
          });
        });
        combo.name = this.comboName;
        setFixedPrice(combo);
        let comboOrSingleProduct;
        if (combo.pricing_type !== "fixed" && this.onlyOneProductLeft(combo)) {
          console.log("saveItem - only one product left in classic menu");
          comboOrSingleProduct = combo.components[0].selected;
          comboOrSingleProduct.uid = combo.uid;
        } else {
          comboOrSingleProduct = combo;
        }

        this.logAudit({
          category: "CART",
          event: "CHANGED_PRODUCT",
          data: comboOrSingleProduct
        });
        let newBasket = this.basket.filter(
          el => el.uid !== comboOrSingleProduct.uid
        );
        newBasket = addProductToBasket(comboOrSingleProduct, newBasket);
        this.setBasket(newBasket);
      } else {
        const item = createNewObject(this.selectedItem);
        this.logAudit({
          category: "CART",
          event: "CHANGED_PRODUCT",
          data: item
        });
        let newBasket = this.basket.filter(el => el.uid !== item.uid);
        newBasket = addProductToBasket(item, newBasket);
        this.setBasket(newBasket);
      }
      setTimeout(() => {
        this.$emit("closeModal");
      }, 500);
    }
  }
};
</script>
<style scoped>
.fixedContainer {
  position: fixed;
  top: 660px;
  width: 100%;
  padding-inline: 70px;
  height: 950px;
}
.modalBlock {
  position: fixed;
  z-index: 200;
  top: 0;
  left: 0;
  width: 1080px;
  height: 1920px;
  background: var(--component_modals_edit_modalBlock_background);
}
.modalBlock-hasBackgroundImage {
  background-image: var(--component_modals_edit_modalBlock_background-image);
}
.BottomNavigationContainer {
  position: absolute;
  z-index: 10;
  width: 100%;
  bottom: 0;
  background: var(--component_modals_edit_BottomNavigationContainer_background);
}
.modalHeaderBlock {
  display: flex;
  justify-content: flex-start;
  height: var(--component_modals_edit_modalHeaderBlock_height);
  align-items: center;
  flex-direction: column;
  isolation: isolate;
  padding-top: var(--component_modals_edit_headerTitle_top);
}

.modalHeaderBlock-isHidden {
  display: none;
}

.ingredientSelectorWrapper {
  background-color: var(
    --component_modals_edit_ingredientSelectorWrapper_background-color
  );
  /*margin: var(--component_modals_edit_ingredientSelectorWrapper_margin);*/
  margin: 0 0 20px 0;
  width: 100%;
}
.changeComboBlock {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: var(--component_modals_edit_changeComboBlock_padding);
}

.half {
  min-height: 120px;
  margin: 0;
}

.headerTitle {
  font-family: var(--component_modals_edit_headerTitle_font-family);
  font-size: var(--component_modals_edit_headerTitle_font-size);
  letter-spacing: 0;
  color: var(--component_modals_edit_headerTitle_color);
  text-transform: var(--component_modals_edit_headerTitle_text-transform);
  text-shadow: var(--component_modals_edit_headerTitle_text-shadow);
  transform: var(--component_modals_edit_headerTitle_transform);
  position: relative;
  margin: var(--component_modals_edit_headerTitle_margin);
  z-index: 20;
  text-align: center;
}

.header-composition {
  width: 100%;
  position: absolute;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.headerImg {
  height: 330px;
  z-index: 20;
  margin-top: 150px;
}

.yellowCircle {
  position: absolute;
  top: -420px;
  width: 955px;
  height: 919px;
  border-radius: 50%;
  background: var(--component_modals_edit_yellowCircle_background);
}

.yellowCircle-hasBackgroundImage {
  background-image: var(--component_modals_edit_yellowCircle_background-image);
  background-size: var(--component_modals_edit_yellowCircle_background_size);
  background-repeat: no-repeat;
  background-position: center;
}

.actionBlock {
  background: var(--main-menu_background);
  width: 100%;
  height: 120px;
  display: flex;
  justify-content: space-between;
  letter-spacing: 2px;
  padding: var(--component_modals_edit_actionBlock_padding);
  margin: 0;
  z-index: 10;
  button {
    letter-spacing: 2px;
  }
}
.actionBlock-v2 {
  background: #f6f6f6 linear-gradient(180deg, #efefef 0%, #f7f7f700 10%) 0 0
    no-repeat padding-box;
  bottom: 135px;
  height: 155px;
  padding: 45px 60px 0 60px;
  position: absolute;
}
.actionBlock-v2-disabled {
  display: none;
}
.headerSave {
  font-family: var(--component_modals_edit_headerSave_font-family);
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: var(--component_modals_edit_headerSave_font-size);
  width: var(--component_modals_edit_headerSave_width);
  height: var(--component_modals_edit_headerSave_height);
  background: var(--component_modals_edit_headerSave_background);
  border: var(--component_modals_edit_headerSave_border);
  border-radius: var(--component_modals_edit_headerSave_border-radius);
  color: var(--component_modals_edit_headerSave_color);
  outline: none;
  padding: var(--component_modals_edit_headerSave_padding);
  text-transform: var(--component_modals_edit_headerSave_text-transform);
}
.headerSave:disabled {
  filter: saturate(0);
}
.closeBtn {
  width: var(--component_modals_edit_closeBtn_width);
  height: var(--component_modals_edit_closeBtn_height);
  background: var(--component_modals_edit_closeBtn_background);
  border: var(--component_modals_edit_closeBtn_border);
  border-radius: var(--component_modals_edit_closeBtn_border-radius);
  color: var(--component_modals_edit_closeBtn_color);
  font-family: var(--component_modals_edit_closeBtn_font-family);
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: var(--component_modals_edit_closeBtn_font-size);
  padding: var(--component_modals_edit_closeBtn_padding);
  text-transform: var(--component_modals_edit_closeBtn_text-transform);
}
.comboHeader {
  color: var(--component_modals_edit_comboHeader_color);
  display: flex;
  font-size: var(--component_modals_edit_comboHeader_font-size);
  font-family: var(--component_modals_edit_comboHeader_font-family);
  text-transform: var(--component_modals_edit_comboHeader_text-transform);
  width: 100%;
  height: var(--component_modals_edit_comboHeader_height);
  justify-content: center;
  align-items: flex-start;
  margin: var(--component_modals_edit_comboHeader_margin);
  padding: var(--component_modals_edit_comboHeader_padding);
}
.productBlock {
  flex-grow: 1;
  justify-content: center;
  display: flex;
  margin: var(--component_modals_edit_productBlock_margin);
  padding-inline: 60px;
}
.productBlock-v2 {
  margin: var(--component_modals_edit_productBlock-v2_margin);
}
</style>
